import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, HelpCircle, Search } from "lucide-react";

const FAQ_ITEMS = [
  {
    cat: "For Professionals",
    q: "Is SecuSource really free for professionals?",
    a: "Yes — €0 to register, €0 monthly, no commission on your hourly rate. Ever. The hiring company pays a small transparent platform fee (€1/hr per worker OR 3% of the booking, whichever is lower) — you keep 100% of your negotiated rate.",
  },
  {
    cat: "For Professionals",
    q: "Do I have to provide a reference to join?",
    a: "Yes. Every professional lists at least one previous employer as a reference during registration. SecuSource emails the referee directly to confirm the working relationship and rate your reliability, timekeeping, conduct and integrity. This is what earns you the Reference Verified badge — companies use it to shortcut trust when booking.",
  },
  {
    cat: "For Professionals",
    q: "Can companies see my name and phone number straight away?",
    a: "No. All profiles are anonymous by default — your SecuSource ID, county, roles, kit, qualifications, ratings and endorsements are visible, but your full name, phone, email and address stay hidden until a company formally requests a booking and confirms it.",
  },
  {
    cat: "For Professionals",
    q: "What is the 3-strike reliability policy?",
    a: "Strike 1 (warning) → Strike 2 (hidden from anonymous search for 14 days + training required) → Strike 3 (suspended pending appeal). Applies to no-shows, contract failures, or serious conduct issues. See the full Reliability Policy page.",
  },
  {
    cat: "For Professionals",
    q: "How do cluster teams work?",
    a: "A verified lead pro can create a cluster — a small team that companies can book as one unit (kit, medical, comms and vehicles combined). Members join by SecuSource ID. Endorsements flow to both the team and every individual member's profile.",
  },
  {
    cat: "For Professionals",
    q: "Can I work in both Ireland and the UK?",
    a: "Yes — SecuSource is dual-country. On registration you declare which country you reside in, which countries you're available to work in, and whether you have the legal right to work in each. Jobs are automatically filtered so you only see roles you can legally take.",
  },
  {
    cat: "For Companies",
    q: "How much does it cost?",
    a: "There's no cost to register, no subscription and no listing fee. On each booking you pay ONE platform fee — €1 per hour per worker OR 3% of the booking value, whichever is lower. Automatically applied and shown before you confirm.",
  },
  {
    cat: "For Companies",
    q: "What happens if a professional doesn't turn up?",
    a: "You file a one-tap No-show Report from the booking screen. The pro is immediately QA-flagged (Strike 1 minimum), the booking is locked so they can't mark it complete, admin is emailed, and you get an instant replacement-suggestion list of top-rated available pros in the same county.",
  },
  {
    cat: "For Companies",
    q: "Can I trust the licences shown on a profile?",
    a: "Yes — but also verify them yourself. PSA (Ireland) and SIA (UK) licence numbers are on file with SecuSource admin, and licence status (verified / expired / on file) is shown on every profile. Companies can also use the public /verify-badge page to lookup any licence number before deployment day.",
  },
  {
    cat: "For Companies",
    q: "How do I see contact details?",
    a: "Contact details unlock only after you request a booking and it's approved. Before that, everything is anonymous — profiles show capability, not identity. This protects staff and stops unpaid poaching.",
  },
  {
    cat: "For Companies",
    q: "How fast can I book a team?",
    a: "Search results show anonymous profiles instantly. Once you register (approval usually same day), you can book an individual or full cluster in under 3 minutes.",
  },
  {
    cat: "Trust & safety",
    q: "How does SecuSource verify professionals?",
    a: "Three layers: (1) PSA / SIA licence number on file, admin-verified with document uploads; (2) at least one previous-employer reference, confirmed by email; (3) endorsements from companies after every completed booking, feeding a rolling reliability score.",
  },
  {
    cat: "Trust & safety",
    q: "Are documents private?",
    a: "Yes. PSA / SIA licence files and qualification documents are visible only to SecuSource admin. Companies see the STATUS (verified / expired / on file) but not the file itself, unless the pro explicitly authorises release.",
  },
  {
    cat: "Trust & safety",
    q: "Is SecuSource GDPR-compliant?",
    a: "Yes. Personal data is stored securely, referee contact details are used only for the reference-verification exchange, and any user can request full data export or deletion via /contact.",
  },
  {
    cat: "Platform",
    q: "What countries does SecuSource cover?",
    a: "Ireland and the United Kingdom, with cross-border bookings supported for pros who confirm right-to-work in both jurisdictions.",
  },
  {
    cat: "Platform",
    q: "How do I contact support?",
    a: "Send us a message from the /contact page or email support directly. Owner escalations go straight to the SecuSource admin inbox.",
  },
];

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`card-flat p-5 transition-colors ${open ? "bg-brand-light/40" : ""}`} data-testid={`faq-item-${idx}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left flex items-start justify-between gap-4" data-testid={`faq-toggle-${idx}`}>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold mb-1">{item.cat}</div>
          <div className="font-outfit font-semibold text-white text-base leading-tight">{item.q}</div>
        </div>
        <ChevronDown className={`w-5 h-5 text-white/50 flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 text-sm text-white/70 font-manrope leading-relaxed" data-testid={`faq-answer-${idx}`}>{item.a}</div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? FAQ_ITEMS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q) || f.cat.toLowerCase().includes(q))
    : FAQ_ITEMS;

  const cats = [...new Set(filtered.map((f) => f.cat))];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-8">
      <div>
        <div className="inline-flex items-center gap-2 bg-brand-light text-brand border border-brand/20 px-3 py-1 rounded-full text-xs font-outfit font-bold uppercase tracking-widest mb-4">
          <HelpCircle className="w-3.5 h-3.5" /> Frequently asked questions
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-outfit font-bold text-white tracking-tight leading-[1.05]">
          Everything you might ask before signing up.
        </h1>
        <p className="text-white/70 mt-4 font-manrope text-base sm:text-lg">
          Can&apos;t find your answer? <Link to="/contact" className="text-brand font-semibold">Contact us</Link> — we reply within one working day.
        </p>
      </div>

      <div className="relative">
        <Search className="w-5 h-5 text-white/50 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the FAQ…"
          className="pl-10 h-12"
          data-testid="faq-search"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="card-flat p-10 text-center text-white/60 font-manrope">
          No matches. Try broader keywords — or <Link to="/contact" className="text-brand font-semibold">contact us</Link>.
        </div>
      ) : (
        cats.map((cat) => (
          <div key={cat} className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-white/60 font-outfit font-bold">{cat}</div>
            <div className="space-y-2">
              {filtered.filter((f) => f.cat === cat).map((f) => {
                const globalIdx = FAQ_ITEMS.indexOf(f);
                return <FaqItem key={globalIdx} item={f} idx={globalIdx} />;
              })}
            </div>
          </div>
        ))
      )}

      <div className="rounded-xl bg-navy-900 text-white p-8 flex flex-wrap items-center justify-between gap-6">
        <div className="max-w-xl">
          <div className="font-outfit font-bold text-2xl">Still have questions?</div>
          <p className="text-slate-300 font-manrope text-sm mt-2">The team behind SecuSource is a small group of working security professionals. We reply personally.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild className="bg-brand hover:bg-brand-hover text-white"><Link to="/contact" data-testid="faq-contact-cta">Contact us</Link></Button>
          <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10"><Link to="/reliability-policy">Reliability policy</Link></Button>
        </div>
      </div>
    </div>
  );
}
